import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { BD, TX, TX2, GR, YL, RD } from "../utils/constants";
import { fmt } from "../utils/formatUtils";
import { getNextMarketOpen } from "../utils/marketUtils";

function FlashPrice({ value, formatter, isIndex }) {
  const prevRef = useRef(value);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    if (value == null) return;
    if (prevRef.current != null && value !== prevRef.current) {
      setFlash(value > prevRef.current ? "up" : "down");
      const t = setTimeout(() => setFlash(null), 700);
      prevRef.current = value;
      return () => clearTimeout(t);
    }
    prevRef.current = value;
  }, [value]);

  if (value == null) return <span style={{ fontSize: 10, fontWeight: 600, color: TX }}>--</span>;
  const color = flash === "up" ? GR : flash === "down" ? RD : TX;
  return (
    <motion.span
      key={value}
      initial={{ scale: flash ? 1.12 : 1 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.25 }}
      style={{ fontSize: 10, fontWeight: 600, color, transition: "color 0.4s", whiteSpace: "nowrap" }}
    >
      {isIndex ? value.toFixed(1) : `$${value.toFixed(2)}`}
    </motion.span>
  );
}

export default function MarketBar({ marketData, marketOpen }) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 760);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const items = [["S&P (SPY)", marketData?.spy, false], ["NDX (QQQ)", marketData?.qqq, false], ["VIX", marketData?.vix, true]];

  return (
    <>
      {marketData && (
        isMobile ? (
          <div className="ts-panel" style={{ background: "rgba(10,16,32,0.94)", borderBottom: `1px solid ${BD}`, padding: "7px 10px", display: "flex", gap: 7, alignItems: "stretch", overflowX: "auto", maxWidth: "100vw" }}>
            {items.map(([label, d, isIndex]) => (
              <div key={label} style={{ flex: "0 0 108px", background: "#081225", border: `1px solid ${BD}`, borderRadius: 7, padding: "6px 8px" }}>
                <div style={{ fontSize: 7, color: TX2, letterSpacing: 1, marginBottom: 3 }}>{label}</div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 5 }}>
                  <FlashPrice value={d.price} isIndex={isIndex} />
                  {d.change != null && <span style={{ fontSize: 8, fontWeight: 600, color: d.change >= 0 ? GR : RD }}>{fmt(d.change)}</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="ts-panel" style={{ background: "rgba(10,16,32,0.94)", borderBottom: `1px solid ${BD}`, padding: "6px 18px", display: "flex", gap: 22, alignItems: "center", overflowX: "auto", maxWidth: "100vw", whiteSpace: "nowrap" }}>
            <span style={{ fontSize: 8, color: TX2, letterSpacing: 2, flex: "0 0 auto" }}>MARKET</span>
            {[["S&P 500 (SPY)", marketData.spy, false], ["NASDAQ-100 (QQQ)", marketData.qqq, false], ["VIX", marketData.vix, true]].map(([label, d, isIndex]) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 7, flex: "0 0 auto" }}>
                <span style={{ fontSize: 8, color: TX2 }}>{label}</span>
                <FlashPrice value={d.price} isIndex={isIndex} />
                {d.isDelayed && <span style={{ fontSize: 6, color: TX2, letterSpacing: 1 }}>PREV</span>}
                {d.change != null && <span style={{ fontSize: 9, fontWeight: 600, color: d.change >= 0 ? GR : RD }}>{fmt(d.change)}</span>}
              </div>
            ))}
            <div style={{ flex: "0 0 12px" }} />
            {marketData.vix?.price != null && (
              <span style={{ fontSize: 8, color: TX2, flex: "0 0 auto" }}>
                VIX {marketData.vix.price >= 30 ? "EXTREME - high caution" : marketData.vix.price >= 20 ? "ELEVATED - cautious" : "CALM"}
              </span>
            )}
          </div>
        )
      )}

      {!marketOpen && (
        <div style={{ background: "rgba(24,18,5,0.92)", borderBottom: `1px solid ${YL}30`, padding: "6px 18px", display: "flex", alignItems: "center", gap: 10, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <span style={{ fontSize: 7, letterSpacing: 2, color: YL, background: `${YL}15`, border: `1px solid ${YL}40`, borderRadius: 4, padding: "2px 7px" }}>CLOSED</span>
          <span style={{ fontSize: 9, color: TX2 }}>Market is currently closed. Opens {getNextMarketOpen()}.</span>
          <span style={{ fontSize: 9, color: TX2, marginLeft: isMobile ? 0 : "auto" }}>Scan results show last-session data.</span>
        </div>
      )}
    </>
  );
}
